/**
 * Safety Inspection Drawer
 * Create / Edit safety inspections with dynamic inspection items grid.
 */

import { Plus, Trash2, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DatePicker, Input, SearchableDropdown, TextArea } from '../forms';
import { useToast } from '../../contexts/ToastContext';
import {
  useCreateInspectionMutation,
  useUpdateInspectionMutation,
} from '../../services/safetyApi';
import type {
  SafetyInspection,
  InspectionItem,
  InspectionItemPriority,
  InspectionItemStatus,
  InspectionType,
  CreateInspectionRequest,
  UpdateInspectionRequest,
} from '../../types/safety';

// ============================================================================
// Constants
// ============================================================================

const INSPECTION_TYPE_OPTIONS = [
  { value: 'site', label: 'Site' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'scaffold', label: 'Scaffold' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'excavation', label: 'Excavation' },
  { value: 'fire_safety', label: 'Fire Safety' },
  { value: 'housekeeping', label: 'Housekeeping' },
  { value: 'ppe', label: 'PPE' },
  { value: 'other', label: 'Other' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Default categories for quick entry
const DEFAULT_CATEGORIES = [
  'Fall Protection',
  'PPE',
  'Housekeeping',
  'Electrical',
  'Scaffolding',
  'Fire Safety',
  'First Aid',
  'Equipment',
  'Signage',
  'General',
];

// ============================================================================
// Props
// ============================================================================

interface SafetyInspectionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  inspection?: SafetyInspection | null;
  mode: 'create' | 'edit';
}

// ============================================================================
// Helpers
// ============================================================================

const getItemStatusColor = (status: InspectionItemStatus): string => {
  switch (status) {
    case 'pass':
      return 'bg-green-100 text-green-700 border-green-300';
    case 'fail':
      return 'bg-red-100 text-red-700 border-red-300';
    case 'na':
      return 'bg-gray-100 text-gray-600 border-gray-300';
    case 'corrective_action':
      return 'bg-orange-100 text-orange-700 border-orange-300';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-300';
  }
};

const getPriorityColor = (priority: InspectionItemPriority): string => {
  switch (priority) {
    case 'critical':
      return 'text-red-600';
    case 'high':
      return 'text-orange-600';
    case 'medium':
      return 'text-yellow-600';
    case 'low':
      return 'text-green-600';
    default:
      return 'text-gray-600';
  }
};

const buildEmptyItem = (): Omit<InspectionItem, '_id'> => ({
  category: '',
  item: '',
  status: 'pass',
  notes: '',
  photoUrl: '',
  priority: 'medium',
});

// ============================================================================
// Component
// ============================================================================

const SafetyInspectionDrawer: React.FC<SafetyInspectionDrawerProps> = ({
  isOpen,
  onClose,
  projectId,
  inspection,
  mode,
}) => {
  const toast = useToast();

  // Form state
  const [inspectionDate, setInspectionDate] = useState('');
  const [inspectionType, setInspectionType] = useState<InspectionType>('site');
  const [inspectorName, setInspectorName] = useState('');
  const [location, setLocation] = useState('');
  const [area, setArea] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<Omit<InspectionItem, '_id'>[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [createInspection, { isLoading: isCreating }] = useCreateInspectionMutation();
  const [updateInspection, { isLoading: isUpdating }] = useUpdateInspectionMutation();

  const isSubmitting = isCreating || isUpdating;

  // Populate form when editing
  useEffect(() => {
    if (isOpen && inspection && mode === 'edit') {
      setInspectionDate(inspection.inspectionDate?.split('T')[0] || '');
      setInspectionType(inspection.inspectionType);
      setInspectorName(inspection.inspectorName || '');
      setLocation(inspection.location || '');
      setArea(inspection.area || '');
      setNotes(inspection.notes || '');
      setItems(
        inspection.inspectionItems.map(i => ({
          category: i.category,
          item: i.item,
          status: i.status,
          notes: i.notes || '',
          photoUrl: i.photoUrl || '',
          priority: i.priority,
        }))
      );
    } else if (isOpen && mode === 'create') {
      setInspectionDate(new Date().toISOString().split('T')[0]);
      setInspectionType('site');
      setInspectorName('');
      setLocation('');
      setArea('');
      setNotes('');
      setItems([]);
      setErrors({});
    }
  }, [isOpen, inspection, mode]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!inspectionDate) errs.inspectionDate = 'Inspection date is required';
    if (!inspectionType) errs.inspectionType = 'Inspection type is required';
    if (!location.trim()) errs.location = 'Location is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAddItem = useCallback(() => {
    setItems(prev => [...prev, buildEmptyItem()]);
  }, []);

  const handleRemoveItem = useCallback((index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleItemChange = useCallback(
    (index: number, field: keyof Omit<InspectionItem, '_id'>, value: string) => {
      setItems(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    },
    []
  );

  const handleAddDefaultItems = useCallback((category: string) => {
    const defaults: Omit<InspectionItem, '_id'>[] = [
      { category, item: 'Adequate controls in place', status: 'pass', priority: 'medium' },
      { category, item: 'Equipment in good condition', status: 'pass', priority: 'medium' },
      { category, item: 'Signage/labeling proper', status: 'pass', priority: 'low' },
    ];
    setItems(prev => [...prev, ...defaults]);
  }, []);

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const payload: CreateInspectionRequest | UpdateInspectionRequest = {
        inspectionDate,
        inspectionType,
        inspectorName: inspectorName || undefined,
        location,
        area: area || undefined,
        notes: notes || undefined,
        inspectionItems: items.filter(i => i.item.trim() !== ''),
      };

      if (mode === 'create') {
        await createInspection({
          projectId,
          data: payload as CreateInspectionRequest,
        }).unwrap();
        toast.success('Inspection created successfully');
      } else if (inspection) {
        await updateInspection({
          projectId,
          inspectionId: inspection._id,
          data: payload as UpdateInspectionRequest,
        }).unwrap();
        toast.success('Inspection updated successfully');
      }

      onClose();
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'data' in err &&
        typeof (err as { data?: { message?: string } }).data?.message === 'string'
          ? (err as { data: { message: string } }).data.message
          : 'Failed to save inspection';
      toast.error(message);
    }
  };

  // Calculate live score preview
  const scorableItems = items.filter(i => i.status !== 'na' && i.item.trim() !== '');
  const passItems = items.filter(i => i.status === 'pass' && i.item.trim() !== '');
  const liveScore =
    scorableItems.length > 0 ? Math.round((passItems.length / scorableItems.length) * 100) : 100;

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-yellow-600';
    if (score >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer - 90% width for large multi-section drawer */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] inset-y-6 right-6 left-auto w-[90%] max-w-6xl animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {mode === 'create' ? 'New Safety Inspection' : 'Edit Inspection'}
              </h2>
              {inspection && (
                <p className="text-xs text-gray-500 mt-0.5">{inspection.inspectionNumber}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="grid grid-cols-3 h-full divide-x divide-gray-100">
              {/* LEFT PANEL: Details */}
              <div className="col-span-1 px-6 py-6 space-y-4">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Inspection Details
                </h3>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Inspection Date <span className="text-red-500">*</span>
                  </label>
                  <DatePicker
                    value={inspectionDate}
                    onChange={setInspectionDate}
                    placeholder="Select date"
                  />
                  {errors.inspectionDate && (
                    <p className="text-xs text-red-500 mt-1">{errors.inspectionDate}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Inspection Type <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    placeholder="Select type"
                    value={inspectionType}
                    onChange={val => setInspectionType(val as InspectionType)}
                    options={INSPECTION_TYPE_OPTIONS}
                    searchable={false}
                  />
                  {errors.inspectionType && (
                    <p className="text-xs text-red-500 mt-1">{errors.inspectionType}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Inspector Name</label>
                  <Input
                    value={inspectorName}
                    onChange={e => setInspectorName(e.target.value)}
                    placeholder="Inspector name"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                    placeholder="e.g. Building A, Level 3"
                  />
                  {errors.location && (
                    <p className="text-xs text-red-500 mt-1">{errors.location}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Area / Zone</label>
                  <Input
                    value={area}
                    onChange={e => setArea(e.target.value)}
                    placeholder="e.g. North wing, Basement"
                  />
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Notes</label>
                  <TextArea
                    value={notes}
                    onChange={setNotes}
                    placeholder="General inspection notes..."
                    rows={4}
                  />
                </div>

                {/* Live Score Preview */}
                {items.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <p className="text-xs font-medium text-gray-600">Live Score Preview</p>
                    <div className={`text-3xl font-bold ${getScoreColor(liveScore)}`}>
                      {liveScore}%
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="text-green-600">{passItems.length} Pass</span>
                      <span className="text-red-600">
                        {items.filter(i => i.status === 'fail' && i.item.trim()).length} Fail
                      </span>
                      <span className="text-gray-400">
                        {items.filter(i => i.status === 'na' && i.item.trim()).length} N/A
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT PANEL: Inspection Items */}
              <div className="col-span-2 px-6 py-6 flex flex-col space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    Inspection Items
                    <span className="ml-2 text-xs font-normal text-gray-400">
                      ({items.filter(i => i.item.trim()).length} items)
                    </span>
                  </h3>
                  <button
                    onClick={handleAddItem}
                    className="inline-flex items-center text-xs font-medium text-brand-600 hover:text-brand-700"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    Add Item
                  </button>
                </div>

                {/* Quick-add categories */}
                <div>
                  <p className="text-xs text-gray-400 mb-2">Quick add category defaults:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {DEFAULT_CATEGORIES.map(cat => (
                      <button
                        key={cat}
                        onClick={() => handleAddDefaultItems(cat)}
                        className="px-2 py-1 text-xs rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-brand-300 hover:text-brand-600 transition-colors"
                      >
                        + {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Items table */}
                {items.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">No inspection items added</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Use quick-add above or click &quot;Add Item&quot; to start
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 overflow-y-auto space-y-2">
                    {/* Table header */}
                    <div className="grid grid-cols-12 gap-2 text-xs text-gray-400 uppercase tracking-wide px-1 pb-1 border-b border-gray-100">
                      <div className="col-span-2">Category</div>
                      <div className="col-span-4">Item</div>
                      <div className="col-span-2">Status</div>
                      <div className="col-span-1">Priority</div>
                      <div className="col-span-2">Notes</div>
                      <div className="col-span-1"></div>
                    </div>

                    {items.map((item, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-12 gap-2 items-start py-2 border-b border-gray-50 hover:bg-gray-50/50 rounded"
                      >
                        {/* Category */}
                        <div className="col-span-2">
                          <input
                            type="text"
                            value={item.category}
                            onChange={e => handleItemChange(index, 'category', e.target.value)}
                            placeholder="Category"
                            list="category-suggestions"
                            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
                          />
                          <datalist id="category-suggestions">
                            {DEFAULT_CATEGORIES.map(c => (
                              <option key={c} value={c} />
                            ))}
                          </datalist>
                        </div>

                        {/* Item */}
                        <div className="col-span-4">
                          <input
                            type="text"
                            value={item.item}
                            onChange={e => handleItemChange(index, 'item', e.target.value)}
                            placeholder="Inspection item description"
                            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
                          />
                        </div>

                        {/* Status */}
                        <div className="col-span-2">
                          <div className="flex gap-1 flex-wrap">
                            {(['pass', 'fail', 'na', 'corrective_action'] as InspectionItemStatus[]).map(
                              st => (
                                <button
                                  key={st}
                                  onClick={() =>
                                    handleItemChange(index, 'status', st)
                                  }
                                  className={`px-1.5 py-0.5 text-[10px] rounded border font-medium transition-colors ${
                                    item.status === st
                                      ? getItemStatusColor(st)
                                      : 'border-gray-200 text-gray-400 hover:border-gray-300'
                                  }`}
                                >
                                  {st === 'corrective_action' ? 'CA' : st.toUpperCase()}
                                </button>
                              )
                            )}
                          </div>
                        </div>

                        {/* Priority */}
                        <div className="col-span-1">
                          <select
                            value={item.priority}
                            onChange={e =>
                              handleItemChange(
                                index,
                                'priority',
                                e.target.value as InspectionItemPriority
                              )
                            }
                            className={`w-full text-xs border border-gray-200 rounded px-1 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400 ${getPriorityColor(
                              item.priority
                            )}`}
                          >
                            {PRIORITY_OPTIONS.map(p => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Notes */}
                        <div className="col-span-2">
                          <input
                            type="text"
                            value={item.notes || ''}
                            onChange={e => handleItemChange(index, 'notes', e.target.value)}
                            placeholder="Notes..."
                            className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400 focus:border-brand-400"
                          />
                        </div>

                        {/* Remove */}
                        <div className="col-span-1 flex justify-end">
                          <button
                            onClick={() => handleRemoveItem(index)}
                            className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Add more row */}
                    <button
                      onClick={handleAddItem}
                      className="w-full py-2 text-xs text-gray-400 hover:text-brand-600 hover:bg-brand-50 border-2 border-dashed border-gray-200 hover:border-brand-300 rounded-lg transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5 inline mr-1" />
                      Add inspection item
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex items-center justify-between">
            <p className="text-xs text-gray-400">
              {items.filter(i => i.item.trim()).length} items &bull; score will be calculated on save
            </p>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting
                  ? 'Saving...'
                  : mode === 'create'
                  ? 'Create Inspection'
                  : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default SafetyInspectionDrawer;
